import { useState, useEffect } from 'react'
import { supabase } from '../lib/supabase'

function transformProfile(row) {
  return {
    id: row.id,
    name: row.name,
    fullName: row.full_name || row.name,
    username: row.username,
    email: row.email,
    phone: row.phone || '',
    role: row.role,
    organizationId: row.organization_id,
    branchId: row.branch_id,
    isActive: row.is_active,
    mustChangePassword: row.must_change_password,
    createdAt: row.created_at,
    joinedAt: row.created_at ? new Date(row.created_at) : null,
    lastSeenAt: row.last_seen_at ? new Date(row.last_seen_at) : null,
  }
}

export function useUsers(currentUser) {
  const [users, setUsers] = useState([])

  useEffect(() => {
    if (!currentUser) return
    fetchUsers()
  }, [currentUser?.id])

  async function fetchUsers() {
    const { data, error } = await supabase
      .from('profiles')
      .select('*')
      .order('created_at', { ascending: true })
    if (!error && data) setUsers(data.map(transformProfile))
  }

  async function addUser({ name, username, email, role, organizationId, branchId }) {
    const { data: { session } } = await supabase.auth.getSession()
    const res = await fetch(
      `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/create-user`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${session.access_token}`,
        },
        body: JSON.stringify({ name, username, email, role, organizationId, branchId }),
      }
    )
    const json = await res.json()
    if (!res.ok) return { success: false, message: json.error || 'Failed to create user.' }
    await fetchUsers()
    return { success: true, tempPassword: json.tempPassword, user: { username } }
  }

  // Move a user to an org/branch. Server-side (assign-user function) because
  // RLS scoping reads JWT app_metadata, which only the service role can set.
  async function assignUser(userId, { organizationId, branchId }) {
    const { error } = await supabase.functions.invoke('assign-user', {
      body: { userId, organizationId, branchId },
    })
    if (error) {
      // Surface the real reason: our functions return JSON errors, but the
      // edge gateway returns bare text for 401/404 (e.g. UNAUTHORIZED_LEGACY_JWT
      // when Verify JWT wasn't disabled, or NOT_FOUND when not yet deployed).
      let msg = 'Failed to assign user.'
      try {
        const body = await error.context.json()
        if (body?.error) msg = body.error
        else if (body?.message) msg = body.message
      } catch {
        try {
          const text = await error.context.text()
          if (text) msg = `Failed to assign user: ${text.slice(0, 200)}`
        } catch { /* keep default */ }
      }
      return { success: false, message: msg }
    }
    await fetchUsers()
    return { success: true }
  }

  // Change a user's role. Server-side (set-user-role function) for the same
  // reason as assignUser: RLS reads the role from JWT app_metadata, which
  // only the service role can set.
  async function setUserRole(userId, role) {
    const { error } = await supabase.functions.invoke('set-user-role', {
      body: { userId, role },
    })
    if (error) {
      let msg = 'Failed to change role.'
      try {
        const body = await error.context.json()
        if (body?.error) msg = body.error
        else if (body?.message) msg = body.message
      } catch {
        try {
          const text = await error.context.text()
          if (text) msg = `Failed to change role: ${text.slice(0, 200)}`
        } catch { /* keep default */ }
      }
      return { success: false, message: msg }
    }
    await fetchUsers()
    return { success: true }
  }

  async function deactivateUser(userId) {
    await supabase.from('profiles').update({ is_active: false }).eq('id', userId)
    setUsers((prev) => prev.map((u) => (u.id === userId ? { ...u, isActive: false } : u)))
  }

  async function reactivateUser(userId) {
    await supabase.from('profiles').update({ is_active: true }).eq('id', userId)
    setUsers((prev) => prev.map((u) => (u.id === userId ? { ...u, isActive: true } : u)))
  }

  // Bulk activate/deactivate. One statement via .in() — RLS decides which of
  // the ids the caller may actually touch, so no new privileged function is
  // needed. Returns how many rows changed so the UI can report honestly.
  async function setUsersActive(userIds, isActive) {
    if (!userIds?.length) return { success: true, count: 0 }
    const { data, error } = await supabase
      .from('profiles')
      .update({ is_active: isActive })
      .in('id', userIds)
      .select('id')
    if (error) return { success: false, message: error.message }
    const changed = new Set((data || []).map((r) => r.id))
    setUsers((prev) => prev.map((u) => (changed.has(u.id) ? { ...u, isActive } : u)))
    return { success: true, count: changed.size }
  }

  // Bulk password reset. The reset function is per-user, so this fans out and
  // reports the temp passwords it got back alongside any failures.
  async function resetPasswords(userIds) {
    const results = []
    for (const id of userIds) {
      const result = await resetPassword(id)
      results.push({ id, ...result })
    }
    const failed = results.filter((r) => !r.success)
    return {
      success: failed.length === 0,
      count: results.length - failed.length,
      failed: failed.length,
      passwords: results.filter((r) => r.tempPassword).map((r) => ({ id: r.id, tempPassword: r.tempPassword })),
    }
  }

  async function resetPassword(userId) {
    const { data: { session } } = await supabase.auth.getSession()
    const res = await fetch(
      `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/reset-user-password`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${session.access_token}`,
        },
        body: JSON.stringify({ userId }),
      }
    )
    const json = await res.json()
    if (!res.ok) return { success: false }
    setUsers((prev) => prev.map((u) => (u.id === userId ? { ...u, mustChangePassword: true } : u)))
    return { success: true, tempPassword: json.tempPassword }
  }

  async function deleteUser(userId) {
    // Always resolve with a result — never let a thrown fetch (CORS/network/500
    // without headers) leave the caller hanging on a "Deleting…" state.
    const controller = new AbortController()
    const timeout = setTimeout(() => controller.abort(), 30000)
    try {
      const { data: { session } } = await supabase.auth.getSession()
      // The gateway's JWT verification rejects some valid session-token formats
      // (post signing-key migration), so we satisfy the gateway with the anon key
      // and pass the real session token in the body — the function authenticates
      // it against the auth server directly.
      const res = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/delete-user`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`,
            apikey: import.meta.env.VITE_SUPABASE_ANON_KEY,
          },
          body: JSON.stringify({ userId, userToken: session.access_token }),
          signal: controller.signal,
        }
      )
      const json = await res.json().catch(() => ({}))
      if (!res.ok) {
        // Our function returns { error }; the Supabase gateway returns { message, code }
        // (e.g. an expired/invalid JWT). Surface whichever is present.
        const reason = json.error || json.message || json.code
        const message = res.status === 401 && reason
          ? `${reason}. Try signing out and back in.`
          : (reason || `Delete failed (HTTP ${res.status}).`)
        return { success: false, message }
      }
      setUsers((prev) => prev.filter((u) => u.id !== userId))
      return { success: true }
    } catch (e) {
      const msg = e?.name === 'AbortError'
        ? 'The request timed out. The delete function may not be deployed, or it errored — check the function logs.'
        : (e?.message || 'Could not reach the server.')
      return { success: false, message: msg }
    } finally {
      clearTimeout(timeout)
    }
  }

  return {
    users, addUser, assignUser, setUserRole, deactivateUser, reactivateUser,
    resetPassword, deleteUser, setUsersActive, resetPasswords, refetchUsers: fetchUsers,
  }
}
